import { configureStore, PreloadedState } from '@reduxjs/toolkit'
import { persistStore, persistReducer, createTransform } from 'redux-persist'
import set from 'lodash/set'
import storage from 'redux-persist/lib/storage'
import * as Sentry from '@sentry/browser'
import createSentryMiddleware from 'redux-sentry-middleware'
import * as subscribers from './subscribers'

import reducers from '../reducers'

const clearFetchIds = createTransform(
  (pertsistState: Record<string, unknown>) => ({
    ...pertsistState,
  }),
  rehydrateState => ({
    ...rehydrateState,
    fetchId: null,
  }),
  { whitelist: ['client', 'home', 'buyerInfo'] }
)

const NESTED_BLACKLIST_PATHS = {
  // we do not want to cache features
  customerProfile: ['loading', 'data.features'],
  client: ['isGuest'],
}
const blacklistNestedPaths = createTransform(
  (persistState: Record<string, unknown>) => ({
    ...persistState,
  }),
  (rehydrateState, key) => {
    const updatedInboundState = { ...rehydrateState }

    if (NESTED_BLACKLIST_PATHS[key]) {
      NESTED_BLACKLIST_PATHS[key].forEach(path => {
        if (path === 'isGuest') {
          // Always default to is not guest
          set(updatedInboundState, path, false)
        } else if (key === 'customerProfile' && path === 'loading') {
          // Always default to loading
          set(updatedInboundState, path, true)
        } else {
          set(updatedInboundState, path, undefined)
        }
      })
    }

    return updatedInboundState
  }
)

const persistConfig = {
  key: 'root',
  storage,
  transforms: [clearFetchIds, blacklistNestedPaths],
  whitelist: ['client', 'customerProfile', 'home', 'buyerInfo'],
  blacklist: ['trackingSource'],
}

const persistedReducer = persistReducer(persistConfig, reducers)

const sentryMiddleware = createSentryMiddleware(Sentry, {
  stateTransformer: state => ({}),
  actionTransformer: action => {
    const scrubbedAction: any = {}

    if (action) {
      Object.keys(action).forEach(key => {
        if (key === 'type') {
          scrubbedAction.type = action.type
        } else if (typeof action[key] === 'boolean') {
          scrubbedAction[key] = action[key]
        } else if (Array.isArray(action[key])) {
          scrubbedAction[`masked__${key}`] = action[key].map(v => typeof v)
        } else if (typeof action[key] === 'object') {
          scrubbedAction[`masked__${key}`] = '{...}'
        } else {
          scrubbedAction[`masked__${key}`] = typeof action[key]
        }
      })
    }

    return scrubbedAction
  },
})

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware => {
      const includedMiddleware = getDefaultMiddleware({
        // We have a ton of non-serializable values in redux that cause noisy errors.
        // Disable serializable checking to avoid blowing up the console
        serializableCheck: false,
        immutableCheck: {
          // Ignore state paths, e.g. state for 'items':
          ignoredPaths: ['listings.listings.listingsFavorites'],
        },
      })

      if (process.env.NODE_ENV === 'production') {
        return includedMiddleware.concat(sentryMiddleware)
      }

      return includedMiddleware
    },
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState,
  })
}

const store = setupStore()

Object.values(subscribers).forEach(createSubscriber => {
  const subscriber = createSubscriber(store)
  store.subscribe(subscriber)
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof persistedReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = AppStore['dispatch']

export default store
